import React, { useMemo } from 'react';
import { X, Plus, Clock } from 'lucide-react';
import { TodoItem } from '../lib/todos';

interface DayViewModalProps {
    date: Date;
    events: TodoItem[];
    onClose: () => void;
    onAdd: (payload: { dateISO: string }) => void;
    onSelectEvent: (id: string) => void;
}

function ymdLocal(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function isAllDay(iso?: string | null) {
    if (!iso) return true;
    // Treat midnight UTC as all-day in this app's convention
    return iso.endsWith('T00:00:00.000Z');
}

export default function DayViewModal({ date, events, onClose, onAdd, onSelectEvent }: DayViewModalProps) {
    const dayLabel = useMemo(() => date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }), [date]);
    const sorted = useMemo(() => {
        const copy = [...(events || [])];
        copy.sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
        return copy;
    }, [events]);

    const dateISO = ymdLocal(date);

    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />

            {/* Panel */}
            <div className="relative w-full md:max-w-md md:rounded-lg bg-gray-900 border border-gray-800 rounded-t-2xl shadow-2xl p-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-white font-semibold">{dayLabel}</div>
                        <div className="text-xs text-gray-400">{date.toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onAdd({ dateISO })}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-600 border border-blue-500 text-white text-xs"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add
                        </button>
                        <button onClick={onClose} className="p-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800" aria-label="Close day view">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {sorted.length === 0 ? (
                    <div className="text-sm text-gray-400">No events for this day.</div>
                ) : (
                    <div className="space-y-2">
                        {sorted.map(ev => {
                            const allDay = isAllDay(ev.due_date);
                            const time = !allDay && ev.due_date ? new Date(ev.due_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                            return (
                                <button
                                    key={ev.id}
                                    className="w-full text-left px-3 py-2 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700 text-gray-200 flex items-center gap-2"
                                    onClick={() => onSelectEvent(ev.id)}
                                >
                                    {allDay ? (
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 mr-1" />
                                    ) : (
                                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm truncate">{ev.title}</div>
                                        <div className="text-[11px] text-gray-400">{allDay ? 'All day' : time}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
